// The one way into the meetings pages: a "Meetings" button on the Focus
// selection screen (the project's Create / Refine / Implement / Chat
// choice), so a meeting is always one project's (owner decision).
//
// It renders only when BOTH hold, read from `GET /api/meetings/status`:
//   - the feature is on here (`enabled`), and
//   - the user's Google account is connected (`googleConnected`), which
//     happens on the account page, the only place to connect or disconnect.
// With the feature off, the read failing, or Google not connected, the
// screen is exactly what it was without it.
//
// It opens `/focus/meetings?project=<id>`. `useRouter({ warn: false })`, as
// `AccountNavLink` does: Focus renders in component tests that mount no
// router; with none it falls back to a full navigation.

import { useRouter } from "@tanstack/react-router";

import { useT } from "../../i18n/index.js";

import { useMeetingsStatus } from "./useMeetings";

export const MEETINGS_PATH = "/focus/meetings";

/** The query key that names the meetings page's project. */
export const MEETINGS_PROJECT_PARAM = "project";

/** The meetings page of one project. */
export const meetingsHref = (projectId: number): string =>
  `${MEETINGS_PATH}?${new URLSearchParams({ [MEETINGS_PROJECT_PARAM]: String(projectId) }).toString()}`;

/** Where one project's recorded meeting is picked ("Aufgenommenes Meeting verarbeiten"). */
export const MEETINGS_IMPORT_PATH = "/focus/meetings/import";

/** The import page of one project. */
export const meetingsImportHref = (projectId: number): string =>
  `${MEETINGS_IMPORT_PATH}?${new URLSearchParams({ [MEETINGS_PROJECT_PARAM]: String(projectId) }).toString()}`;

/**
 * The project a meetings URL names, or null: a GitLab id or a GitHub one
 * (negative), never 0, never anything but an integer.
 */
export const meetingsProjectOf = (search: string): number | null => {
  const raw = new URLSearchParams(search).get(MEETINGS_PROJECT_PARAM);
  if (raw === null || !/^-?[1-9][0-9]{0,15}$/u.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? id : null;
};

export function MeetingsEntry({ projectId }: { readonly projectId: number }) {
  const t = useT();
  const { status } = useMeetingsStatus();
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;
  if (status === null || !status.enabled || status.googleConnected !== true) return null;
  const href = meetingsHref(projectId);
  const open = (): void => {
    if (router !== undefined) {
      void router.navigate({ href });
      return;
    }
    window.location.assign(href);
  };
  return (
    <div className="meet-entry">
      <button type="button" className="focus-btn is-ghost" onClick={open}>
        {t("meetings.navLabel")}
      </button>
    </div>
  );
}
